/**
 * TOSS Web Application - Sidebar Tooltip
 *
 * Portal tooltip used by the collapsed sidebar (nav rows and the collapse
 * toggle). Extracted verbatim from Sidebar.tsx.
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Simple tooltip component for collapsed sidebar
 */
export function Tooltip({
  children,
  content,
  side = 'right',
  disabled = false,
}: {
  children: React.ReactNode;
  content: string;
  side?: 'right' | 'left';
  disabled?: boolean;
}): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: side === 'right' ? rect.right + 8 : rect.left - 8,
      });
    }
  }, [isVisible, side]);

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={triggerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {isVisible && createPortal(
        <div
          className="fixed z-[9999] px-2 py-1 text-sm rounded-md whitespace-nowrap pointer-events-none animate-fade-in"
          style={{
            top: position.top,
            left: side === 'right' ? position.left : undefined,
            right: side === 'left' ? `calc(100vw - ${position.left}px)` : undefined,
            transform: 'translateY(-50%)',
            backgroundColor: 'var(--color-primary-900)',
            color: 'var(--color-text-on-dark)',
          }}
          role="tooltip"
        >
          {content}
          <div
            className="absolute w-2 h-2 rotate-45 top-1/2 -translate-y-1/2"
            style={{
              backgroundColor: 'var(--color-primary-900)',
              left: side === 'right' ? '-4px' : undefined,
              right: side === 'left' ? '-4px' : undefined,
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
